/**
 * La barra di avanzamento **navigabile** di un film o di un episodio (SPEC §3.4).
 *
 * ## Perché serve, e perché ±10 s non basta
 *
 * Perché su un film di due ore tornare a metà con salti da dieci secondi vuol dire premere
 * trecentosessanta volte. Il player aveva `−10 s` e `+10 s` e nient'altro: una posizione dentro il
 * file non si raggiungeva, si inseguiva.
 *
 * ## Le tre regole che la rendono usabile con un telecomando
 *
 * **Si sposta un cursore, non la riproduzione.** Finché non si conferma, il film continua dov'è: un
 * televisore che riparte da capo a ogni pressione della freccia impiega un secondo e mezzo a
 * ricominciare, e scorrere diventa impossibile. Si conferma con OK, si annulla con Indietro.
 *
 * **Tenendo premuto accelera.** Un tocco vale dieci secondi; tenendo, la velocità sale da 10 s/s
 * fino a un minuto al secondo in circa due secondi e mezzo. Senza accelerazione una barra navigabile
 * è la stessa tortura di prima con un passaggio in più.
 *
 * **Il conto è sul tempo trascorso, non sui passi.** Un contatore che somma una quantità fissa a
 * ogni tick va più piano su un apparecchio lento — cioè proprio dove scorrere costa di più — e la
 * velocità dichiarata a schermo direbbe il falso. Qui si moltiplica per i secondi realmente passati.
 *
 * ## Perché sta nel dominio
 *
 * Perché è aritmetica, e l'aritmetica si prova senza un browser e senza far passare il tempo davvero.
 * Quel che resta fuori è disegnare un pallino su una barra.
 */

/** Quanto vale una pressione singola. Dieci secondi: il salto con cui si riascolta una battuta. */
export const SCRUB_STEP_SEC = 10;

/**
 * Da quanto tempo bisogna tenere premuto perché cominci ad accelerare.
 *
 * Trentacinque centesimi: sotto, ogni tocco un po' lungo diventerebbe una corsa, e non si riuscirebbe
 * più a spostarsi di dieci secondi esatti — che è il gesto più comune dei due.
 */
export const SCRUB_ACCEL_DELAY_SEC = 0.35;

/** Da dove parte la velocità quando comincia ad accelerare, in secondi di film al secondo reale. */
export const SCRUB_MIN_SPEED = 10;

/**
 * Il massimo: un minuto di film al secondo.
 *
 * Oltre, su un film di due ore si attraversa tutto in meno di due secondi e il cursore diventa
 * impossibile da fermare dove si vuole.
 */
export const SCRUB_MAX_SPEED = 60;

/** Di quanto cresce la velocità ogni secondo di pressione. Porta da 10 a 60 in ~2,5 s. */
export const SCRUB_ACCEL_PER_SEC = 20;

/**
 * Il passo più lungo che si accetta in un solo avanzamento.
 *
 * Serve contro le pause del motore: un televisore che si ferma due secondi per una riscossione della
 * memoria produrrebbe un salto di due minuti in un colpo, e il cursore sparirebbe dallo schermo. Si
 * tronca a mezzo secondo: si perde un po' di corsa, non la posizione.
 */
export const SCRUB_MAX_STEP_SEC = 0.5;

/**
 * Quanto può passare fra due pressioni perché contino come **una tenuta**.
 *
 * Il telecomando non manda «tasto premuto» e «tasto rilasciato»: manda la stessa pressione ripetuta
 * dalla ripetizione automatica del sistema, una ogni trenta millisecondi circa. Tenere premuto e
 * premere in fretta sono quindi la stessa sequenza di eventi, e l'unica differenza misurabile è il
 * buco fra l'uno e l'altro.
 *
 * Trecento millisecondi: più della ripetizione automatica di qualunque piattaforma, meno di quanto
 * ci mette una persona a ripremere. Sopra questa soglia si ricomincia da capo — cioè il tocco
 * successivo vale di nuovo dieci secondi esatti.
 */
export const SCRUB_HOLD_GAP_MS = 300;

export interface ScrubState {
  /** Dove sta il cursore adesso, in secondi. */
  readonly positionSec: number;
  /** Da dove si era partiti: è la tacca bianca, e il punto a cui Indietro riporta. */
  readonly fromSec: number;
  /** `1` avanti, `-1` indietro. */
  readonly direction: 1 | -1;
  /** La velocità corrente, in secondi di film al secondo. `0` a tasto rilasciato. */
  readonly speed: number;
}

/** La velocità dopo `heldSec` di pressione continua. */
export function scrubSpeedAt(heldSec: number): number {
  if (heldSec < SCRUB_ACCEL_DELAY_SEC) return SCRUB_MIN_SPEED;
  return Math.min(SCRUB_MAX_SPEED, SCRUB_MIN_SPEED + (heldSec - SCRUB_ACCEL_DELAY_SEC) * SCRUB_ACCEL_PER_SEC);
}

/** Dentro il file: mai sotto zero, mai sull'ultimo fotogramma — da lì non si riparte. */
function clamp(positionSec: number, durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  return Math.max(0, Math.min(durationSec - 1, positionSec));
}

/**
 * Comincia a scorrere: il primo tocco vale `SCRUB_STEP_SEC`.
 *
 * Se si stava già scorrendo si riparte **da dov'è il cursore**, non dalla posizione della
 * riproduzione: altrimenti cambiare direzione a metà corsa riporterebbe indietro tutto.
 */
export function scrubStart(
  current: ScrubState | null,
  playbackSec: number,
  durationSec: number,
  direction: 1 | -1,
): ScrubState {
  const base = current?.positionSec ?? playbackSec;
  return {
    positionSec: clamp(base + direction * SCRUB_STEP_SEC, durationSec),
    // Da dove si era partiti **la prima volta**: è il punto a cui Indietro riporta, e non deve
    // spostarsi cambiando direzione a metà corsa.
    fromSec: current?.fromSec ?? playbackSec,
    direction,
    speed: SCRUB_MIN_SPEED,
  };
}

/**
 * Un passo di corsa, dopo `heldSec` di pressione e `elapsedSec` dall'ultimo passo.
 *
 * Non accelera finché non è passato `SCRUB_ACCEL_DELAY_SEC`: prima di allora il cursore resta dove
 * l'ha messo `scrubStart`, cioè un tocco breve vale esattamente dieci secondi.
 */
export function scrubAdvance(
  state: ScrubState,
  heldSec: number,
  elapsedSec: number,
  durationSec: number,
): ScrubState {
  if (heldSec < SCRUB_ACCEL_DELAY_SEC) return state;
  const speed = scrubSpeedAt(heldSec);
  const step = state.direction * speed * Math.min(Math.max(elapsedSec, 0), SCRUB_MAX_STEP_SEC);
  return { ...state, positionSec: clamp(state.positionSec + step, durationSec), speed };
}

/** Tasto rilasciato: il cursore resta dov'è, e la velocità sparisce dallo schermo. */
export function scrubRelease(state: ScrubState): ScrubState {
  return state.speed === 0 ? state : { ...state, speed: 0 };
}

/**
 * Come si scrive la velocità accanto al cursore: `▶▶ 24 s/s`, `◀◀ 1 min/s`.
 *
 * Restituisce le parti, non la frase: il «s/s» e il «1 min/s» sono testo di prodotto e li compone
 * l'i18n in sette lingue. Qui c'è solo il numero e da che parte si sta andando.
 */
export function scrubSpeedLabel(state: ScrubState): { readonly forward: boolean; readonly perSecond: number } | null {
  if (state.speed <= 0) return null;
  return { forward: state.direction > 0, perSecond: Math.round(state.speed) };
}

/**
 * Il tempo in `m:ss` oppure `h:mm:ss`, come il disegno.
 *
 * I minuti e basta — «6 min» — rispondono a «quanto manca» e bastano ai capi della barra; per
 * **scegliere una posizione** servono i secondi, altrimenti il cursore si muove e il numero no.
 */
export function timecode(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.round(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
